import { Box, Switch, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect } from 'react';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GET_RESOURCES_GRID_VIEW } from './admin-console-resources.gql';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
  })
);

const AdminConsoleResourcesPanel = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [gridViewEnabled, setGridViewEnabled] = React.useState(false);

  const { data: gridViewData, refetch: refetchGridView } = useCampfireQuery(GET_RESOURCES_GRID_VIEW);

  useEffect(() => {
    setGridViewEnabled((gridViewData as any)?.vm?.configItem?.configItemValue?.flagValue ?? false);
  }, [gridViewData]);

  const onGridViewChange = () => {
    const previousValue = gridViewEnabled;
    const nextValue = !gridViewEnabled;
    setGridViewEnabled(nextValue);

    const showError = () => {
      setGridViewEnabled(previousValue);
      setSnackbar({
        variant: 'error',
        open: true,
        message: 'Unable to update settings',
      });
    };

    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: nextValue,
          type: 'resources-grid-view',
        },
      })
      .then((response) => {
        if (!response.ok) {
          showError();
          return;
        }
        if (refetchGridView) {
          setTimeout(refetchGridView, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Settings updated',
        });
      })
      .catch(showError);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Typography variant='h2' className={classes.headingTitle}>
          Resources
        </Typography>
        <Typography variant='body2'>{'Manage settings for the Resources section'}</Typography>
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <Typography style={{ fontWeight: 600, fontSize: '16px' }}>Display</Typography>
        <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
          {'Options relating to how resources are displayed to volunteers'}
        </Typography>
        <Box marginTop='1.5rem'>
          <Box mt={2}>
            <Box display='flex' alignItems='center'>
              <Switch
                checked={gridViewEnabled}
                onChange={onGridViewChange}
                color='primary'
                inputProps={{ 'aria-label': 'grid view toggle' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Grid View
                </Typography>
                <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
                  {'Display resource categories as a card grid instead of a list view'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { AdminConsoleResourcesPanel };
